import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import type { TableColumnsType } from 'antd'
import {
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import { deviceCatalogApi } from '@/services/deviceCatalog'
import styles from './DeviceCatalogManagement.module.css'
import type {
  AdminDeviceTemplateDetail,
  AdminDeviceTemplateListItem,
  DeviceCatalogDeviceAdmin,
  DeviceCatalogFunctionAdmin,
  DeviceTemplate,
  DeviceTemplateFunction,
  DeviceTemplateResourceAxis,
  DeviceCatalogSnapshot,
  DeviceCatalogToken,
  DeviceCatalogTokenCreated,
} from '@/types/deviceCatalog'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

interface DeviceEditValues {
  display_name?: string
  template_id?: string | null
  category?: string
  device_group?: string
  tags?: string[]
  user_visible?: boolean
  status?: DeviceCatalogDeviceAdmin['status']
  environment?: string
  approved_for_editor?: boolean
  editor_placeable?: boolean
  protocol?: string
  mobility?: string
  notes?: string
}

interface DeviceFilterValues {
  editorPlaceable: 'all' | 'placeable' | 'non_placeable'
  visibility: 'all' | 'visible' | 'hidden'
  sourceState: 'active' | 'inactive' | 'all'
  deviceGroup: string
}

interface FunctionEditValues {
  label?: string
  description?: string
  status?: DeviceCatalogFunctionAdmin['status']
  approved_for_editor?: boolean
  safety_level?: DeviceCatalogFunctionAdmin['safety_level']
  dry_run_supported?: boolean
}

interface TemplateEditValues {
  id: string
  name: string
  category: string
  tags: string[]
  accepted_resource_axes: DeviceTemplateResourceAxis[]
  editor_plugin_id?: string
  enabled: boolean
  functions_json: string
}

interface TokenCreateValues {
  device_no: string
  client_type: string
  environment: string
  merchant_id?: number
  venue_id?: number
  room_id?: number
  scopes: string[]
  expires_in_days: number
  note?: string
}

const statusColor: Record<string, string> = {
  registered: 'gold',
  tested: 'green',
  disabled: 'red',
  offline: 'default',
}

const safetyColor: Record<string, string> = {
  safe: 'green',
  normal: 'blue',
  destructive: 'volcano',
  restricted: 'purple',
}

const DEVICE_GROUP_OPTIONS = [
  { value: 'switch', label: '开关类' },
  { value: 'led_matrix', label: 'LED灯阵' },
  { value: 'moving_head', label: '摇头灯' },
  { value: 'display', label: '显示设备' },
  { value: 'audio', label: '音频设备' },
  { value: 'sensor', label: '传感设备' },
  { value: 'positioning', label: '定位设备' },
  { value: 'other', label: '其他设备' },
]

const DEVICE_TAG_OPTIONS = [
  '开关类',
  '泡泡机',
  '风扇',
  '香薰机',
  '烟机',
  '氛围特效',
  '气流特效',
  '气味特效',
  'LED灯阵',
  'LED墙',
  '像素灯光',
  '天花板灯光',
  '圆形灯阵',
  '摇头灯',
  '舞台灯光',
  '可调角度',
  '目标跟随',
  '显示设备',
  '音频设备',
  '传感设备',
  '定位设备',
  '内部设备',
  '不显示',
]

const HIDDEN_DEVICE_TAGS = new Set(['内部设备', '不显示'])

const CLASSIFICATION_SOURCE_LABELS: Record<string, string> = {
  manual_override: '人工维护',
  device_type: '类型识别',
  function_signature: '方法识别',
  name_keyword: '名称识别',
  unclassified: '待确认',
}

const renderDeviceTags = (values: string[]) => {
  const visibleTags = values.slice(0, 4)
  const hiddenCount = Math.max(values.length - visibleTags.length, 0)

  return (
    <div className={styles.tagList}>
      {visibleTags.map((value) => (
        <Tag key={value} color={HIDDEN_DEVICE_TAGS.has(value) ? 'red' : undefined}>
          {value}
        </Tag>
      ))}
      {hiddenCount > 0 && (
        <Tooltip title={values.slice(visibleTags.length).join('、')}>
          <Tag>+{hiddenCount}</Tag>
        </Tooltip>
      )}
    </div>
  )
}

const RESOURCE_AXIS_OPTIONS: Array<{
  value: DeviceTemplateResourceAxis
  label: string
}> = [
  { value: 'audio', label: '音频' },
  { value: 'document', label: '文档' },
  { value: 'html', label: 'HTML' },
  { value: 'image', label: '图片' },
  { value: 'program', label: '程序' },
  { value: 'video', label: '视频' },
]

const EMPTY_FUNCTIONS_JSON = '[]'

const parseTemplateFunctions = (value: string): DeviceTemplateFunction[] => {
  const parsed: unknown = JSON.parse(value)
  if (!Array.isArray(parsed)) {
    throw new Error('函数定义必须是 JSON 数组')
  }
  return parsed.map((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new Error(`第 ${index + 1} 个函数必须是对象`)
    }
    const candidate = item as Record<string, unknown>
    if (typeof candidate.name !== 'string' || !candidate.name.trim()) {
      throw new Error(`第 ${index + 1} 个函数缺少 name`)
    }
    if (typeof candidate.label !== 'string' || !candidate.label.trim()) {
      throw new Error(`第 ${index + 1} 个函数缺少 label`)
    }
    if (
      !candidate.parameters ||
      typeof candidate.parameters !== 'object' ||
      Array.isArray(candidate.parameters)
    ) {
      throw new Error(`第 ${index + 1} 个函数 parameters 必须是 JSON Schema 对象`)
    }
    return {
      name: candidate.name,
      label: candidate.label,
      parameters: candidate.parameters as Record<string, unknown>,
    }
  })
}

const DEFAULT_SCOPES = ['device_catalog:read', 'device_command:validate', 'device_command:dry_run']

const DeviceCatalogManagement: React.FC = () => {
  const [devices, setDevices] = useState<DeviceCatalogDeviceAdmin[]>([])
  const [tokens, setTokens] = useState<DeviceCatalogToken[]>([])
  const [snapshots, setSnapshots] = useState<DeviceCatalogSnapshot[]>([])
  const [latestSnapshot, setLatestSnapshot] = useState<DeviceCatalogSnapshot | null>(null)
  const [templates, setTemplates] = useState<AdminDeviceTemplateListItem[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<AdminDeviceTemplateDetail | null>(null)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [loadingDevices, setLoadingDevices] = useState(false)
  const [loadingTokens, setLoadingTokens] = useState(false)
  const [loadingSnapshots, setLoadingSnapshots] = useState(false)
  const [editingDevice, setEditingDevice] = useState<DeviceCatalogDeviceAdmin | null>(null)
  const [editingFunction, setEditingFunction] = useState<DeviceCatalogFunctionAdmin | null>(null)
  const [deviceFilters, setDeviceFilters] = useState<DeviceFilterValues>({
    editorPlaceable: 'all',
    visibility: 'visible',
    sourceState: 'active',
    deviceGroup: 'all',
  })
  const [devicePagination, setDevicePagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })
  const [tokenModalOpen, setTokenModalOpen] = useState(false)
  const [createdToken, setCreatedToken] = useState<DeviceCatalogTokenCreated | null>(null)
  const [deviceForm] = Form.useForm<DeviceEditValues>()
  const [functionForm] = Form.useForm<FunctionEditValues>()
  const [tokenForm] = Form.useForm<TokenCreateValues>()
  const [templateForm] = Form.useForm<TemplateEditValues>()
  const selectedDeviceTags = Form.useWatch('tags', deviceForm)
  const visibilityLockedByTag = (selectedDeviceTags || []).some((tag) =>
    HIDDEN_DEVICE_TAGS.has(tag)
  )

  const loadDevices = useCallback(async () => {
    setLoadingDevices(true)
    try {
      const result = await deviceCatalogApi.listDevices({
        includeInactive: deviceFilters.sourceState === 'all',
        sourceActive:
          deviceFilters.sourceState === 'all' ? undefined : deviceFilters.sourceState === 'active',
        editorPlaceable:
          deviceFilters.editorPlaceable === 'all'
            ? undefined
            : deviceFilters.editorPlaceable === 'placeable',
        userVisible:
          deviceFilters.visibility === 'all' ? undefined : deviceFilters.visibility === 'visible',
        deviceGroup: deviceFilters.deviceGroup === 'all' ? undefined : deviceFilters.deviceGroup,
        page: devicePagination.current,
        pageSize: devicePagination.pageSize,
      })
      setDevices(result.items)
      setDevicePagination((current) => ({
        ...current,
        current: result.page,
        pageSize: result.page_size,
        total: result.total,
      }))
    } catch (error) {
      message.error('设备能力目录加载失败')
    } finally {
      setLoadingDevices(false)
    }
  }, [deviceFilters, devicePagination.current, devicePagination.pageSize])

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true)
    try {
      const result = await deviceCatalogApi.listTemplates()
      setTemplates(result.items)
    } catch (error) {
      message.error('标准设备模板加载失败')
    } finally {
      setLoadingTemplates(false)
    }
  }, [])

  const loadTokens = useCallback(async () => {
    setLoadingTokens(true)
    try {
      const result = await deviceCatalogApi.listTokens()
      setTokens(result.items)
    } catch (error) {
      message.error('设备目录 Token 加载失败')
    } finally {
      setLoadingTokens(false)
    }
  }, [])

  const loadSnapshots = useCallback(async () => {
    setLoadingSnapshots(true)
    try {
      const result = await deviceCatalogApi.listSnapshots()
      setSnapshots(result.items)
      setLatestSnapshot(result.latest)
    } catch (error) {
      message.error('设备目录同步状态加载失败')
    } finally {
      setLoadingSnapshots(false)
    }
  }, [])

  useEffect(() => {
    void loadDevices()
    void loadTemplates()
    void loadTokens()
    void loadSnapshots()
  }, [loadDevices, loadTemplates, loadTokens, loadSnapshots])

  const handleOpenDevice = useCallback(
    (record: DeviceCatalogDeviceAdmin) => {
      setEditingDevice(record)
      deviceForm.setFieldsValue({
        display_name: record.display_name,
        template_id: record.template_id,
        category: record.category,
        device_group: record.device_group,
        tags: record.tags,
        user_visible: record.user_visible,
        status: record.status,
        environment: record.environment,
        approved_for_editor: record.approved_for_editor,
        editor_placeable: record.editor_placeable,
        protocol: record.protocol || undefined,
        mobility: record.mobility || undefined,
        notes: record.notes,
      })
    },
    [deviceForm]
  )

  const handleOpenTemplate = useCallback(
    async (record: AdminDeviceTemplateListItem) => {
      try {
        const detail = await deviceCatalogApi.getTemplate(record.id)
        setEditingTemplate(detail)
        templateForm.setFieldsValue({
          id: detail.id,
          name: detail.name,
          category: detail.category,
          tags: detail.tags,
          accepted_resource_axes: detail.accepted_resource_axes,
          editor_plugin_id: detail.editor_plugin_id || undefined,
          enabled: detail.enabled,
          functions_json: JSON.stringify(detail.functions, null, 2),
        })
        setTemplateModalOpen(true)
      } catch (error) {
        message.error('模板详情加载失败')
      }
    },
    [templateForm]
  )

  const handleCreateTemplate = useCallback(() => {
    setEditingTemplate(null)
    templateForm.setFieldsValue({
      tags: [],
      accepted_resource_axes: [],
      enabled: true,
      functions_json: EMPTY_FUNCTIONS_JSON,
    })
    setTemplateModalOpen(true)
  }, [templateForm])

  const handleSaveTemplate = async () => {
    const values = await templateForm.validateFields()
    let functions: DeviceTemplateFunction[]
    try {
      functions = parseTemplateFunctions(values.functions_json)
    } catch (error) {
      message.error(error instanceof Error ? error.message : '函数定义格式无效')
      return
    }
    const template: DeviceTemplate = {
      id: values.id.trim(),
      name: values.name.trim(),
      category: values.category.trim(),
      tags: values.tags || [],
      accepted_resource_axes: values.accepted_resource_axes || [],
      functions,
      editor_plugin_id: values.editor_plugin_id?.trim() || null,
      enabled: values.enabled,
    }
    try {
      await deviceCatalogApi.saveTemplate(template)
      message.success('标准设备模板已保存')
      setTemplateModalOpen(false)
      setEditingTemplate(null)
      templateForm.resetFields()
      await Promise.all([loadTemplates(), loadDevices()])
    } catch (error) {
      message.error('标准设备模板保存失败，请检查 Schema 和模板字段')
    }
  }

  const handleSetTemplateEnabled = useCallback(
    async (record: AdminDeviceTemplateListItem, enabled: boolean) => {
      try {
        await deviceCatalogApi.saveTemplate({
          id: record.id,
          name: record.name,
          category: record.category,
          tags: record.tags,
          accepted_resource_axes: record.accepted_resource_axes,
          functions: record.functions,
          editor_plugin_id: record.editor_plugin_id,
          enabled,
        })
        message.success(enabled ? '模板已启用' : '模板已停用')
        await loadTemplates()
      } catch (error) {
        message.error(enabled ? '模板启用失败' : '模板停用失败')
      }
    },
    [loadTemplates]
  )

  const handleDeleteTemplate = useCallback(
    async (record: AdminDeviceTemplateListItem) => {
      try {
        await deviceCatalogApi.deleteTemplate(record.id)
        message.success('设备模板已删除')
        await Promise.all([loadTemplates(), loadDevices()])
      } catch (error) {
        message.error('设备模板删除失败，请确认当前没有设备引用该模板')
      }
    },
    [loadDevices, loadTemplates]
  )

  const handleSaveDevice = async () => {
    if (!editingDevice) return
    const values = await deviceForm.validateFields()
    await deviceCatalogApi.updateDevice(editingDevice.device_id, {
      ...values,
      template_id: values.template_id ?? null,
    })
    message.success('设备目录审批信息已更新')
    setEditingDevice(null)
    deviceForm.resetFields()
    await loadDevices()
  }

  const handleRestoreDeviceClassification = async () => {
    if (!editingDevice) return
    await deviceCatalogApi.updateDevice(editingDevice.device_id, {
      device_group: null,
      tags: null,
      user_visible: null,
    })
    message.success('已恢复智能识别和默认可见性')
    setEditingDevice(null)
    deviceForm.resetFields()
    await loadDevices()
  }

  const handleOpenFunction = useCallback(
    (record: DeviceCatalogFunctionAdmin) => {
      setEditingFunction(record)
      functionForm.setFieldsValue({
        label: record.label,
        description: record.description || undefined,
        status: record.status,
        approved_for_editor: record.approved_for_editor,
        safety_level: record.safety_level,
        dry_run_supported: record.dry_run_supported,
      })
    },
    [functionForm]
  )

  const handleSaveFunction = async () => {
    if (!editingFunction) return
    const values = await functionForm.validateFields()
    await deviceCatalogApi.updateFunction(editingFunction.id, values)
    message.success('设备方法审批信息已更新')
    setEditingFunction(null)
    await loadDevices()
  }

  const handleCreateToken = async () => {
    const values = await tokenForm.validateFields()
    const result = await deviceCatalogApi.createToken({
      ...values,
      owner_type: 'client',
      scopes: values.scopes || DEFAULT_SCOPES,
      expires_in_days: values.expires_in_days || 365,
    })
    setCreatedToken(result)
    message.success('Token 已创建，请立即复制保存')
    await loadTokens()
  }

  const handleRevokeToken = useCallback(
    async (tokenId: string) => {
      await deviceCatalogApi.revokeToken(tokenId)
      message.success('Token 已撤销')
      await loadTokens()
    },
    [loadTokens]
  )

  const functionColumns: TableColumnsType<DeviceCatalogFunctionAdmin> = useMemo(
    () => [
      {
        title: '方法信息',
        key: 'function',
        width: 360,
        render: (_, record) => (
          <div className={styles.functionCell}>
            <Text strong>{record.label || record.function_name}</Text>
            <Text type="secondary" code copyable={{ text: record.function_name }}>
              {record.function_name}
            </Text>
            {record.description && (
              <Tooltip title={record.description}>
                <Text type="secondary" className={styles.functionDescription}>
                  {record.description}
                </Text>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: '审批与安全',
        key: 'availability',
        width: 340,
        render: (_, record) => (
          <div className={styles.scopeCell}>
            <div className={styles.statusRow}>
              <Tag color={statusColor[record.status]}>{record.status}</Tag>
              <Tag color={safetyColor[record.safety_level]}>{record.safety_level}</Tag>
            </div>
            <div className={styles.statusRow}>
              {record.approved_for_editor ? (
                <Tag color="green">编辑器已批准</Tag>
              ) : (
                <Tag>编辑器未批准</Tag>
              )}
              {record.dry_run_supported ? (
                <Tag color="cyan">支持 Dry-run</Tag>
              ) : (
                <Tag>不支持 Dry-run</Tag>
              )}
            </div>
          </div>
        ),
      },
      {
        title: 'Schema',
        dataIndex: 'schema_hash',
        width: 250,
        render: (value: string) => (
          <Tooltip title={value}>
            <Text code copyable={{ text: value }} className={styles.schemaHash}>
              {value}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: '操作',
        width: 100,
        fixed: 'right',
        render: (_, record) => <Button onClick={() => handleOpenFunction(record)}>审批</Button>,
      },
    ],
    [handleOpenFunction]
  )

  const deviceColumns: TableColumnsType<DeviceCatalogDeviceAdmin> = useMemo(
    () => [
      {
        title: '设备信息',
        key: 'device',
        width: 320,
        fixed: 'left',
        render: (_, record) => (
          <div className={styles.deviceCell}>
            <div className={styles.deviceTitleRow}>
              <Tooltip title={record.display_name}>
                <Text strong className={styles.deviceName}>
                  {record.display_name}
                </Text>
              </Tooltip>
              {record.online ? <Tag color="green">在线</Tag> : <Tag>离线</Tag>}
            </div>
            <Tooltip title={record.device_id}>
              <Text
                type="secondary"
                code
                copyable={{ text: record.device_id }}
                className={styles.deviceId}
              >
                {record.device_id}
              </Text>
            </Tooltip>
            <div className={styles.metaRow}>
              <Tag>{record.device_type}</Tag>
              <Text type="secondary" className={styles.secondaryLine}>
                场馆 {record.venue_id}
                {record.room_id ? ` · 房间 ${record.room_id}` : ''}
              </Text>
            </div>
          </div>
        ),
      },
      {
        title: '分类与标签',
        key: 'classification',
        width: 410,
        render: (_, record) => (
          <div className={styles.classificationCell}>
            <div className={styles.classificationHeader}>
              <Tag color="geekblue">{record.device_group_label}</Tag>
              <Tag color="blue">{record.category || '未分类'}</Tag>
              <Text type="secondary" className={styles.secondaryLine}>
                {CLASSIFICATION_SOURCE_LABELS[record.classification_source] ||
                  record.classification_source}
                {' · '}
                {Math.round(record.classification_confidence * 100)}%
              </Text>
            </div>
            {renderDeviceTags(record.tags)}
          </div>
        ),
      },
      {
        title: '可用范围',
        key: 'scope',
        width: 260,
        render: (_, record) => (
          <div className={styles.scopeCell}>
            <div className={styles.statusRow}>
              {record.user_visible ? (
                <Tag color="green">用户显示</Tag>
              ) : (
                <Tag color="red">用户隐藏</Tag>
              )}
              {record.approved_for_editor ? (
                <Tag color="green">编辑器已批准</Tag>
              ) : (
                <Tag>编辑器未批准</Tag>
              )}
            </div>
            <div className={styles.statusRow}>
              {record.editor_placeable ? <Tag color="cyan">地图可摆放</Tag> : <Tag>服务类设备</Tag>}
            </div>
          </div>
        ),
      },
      {
        title: '运行状态',
        key: 'runtime',
        width: 220,
        render: (_, record) => (
          <div className={styles.runtimeCell}>
            <div className={styles.statusRow}>
              {record.source_active !== false ? (
                <Tag color="green">当前设备</Tag>
              ) : (
                <Tag color="default">历史设备</Tag>
              )}
              <Tag color={statusColor[record.status]}>{record.status}</Tag>
              <Tag color="blue">{record.environment}</Tag>
            </div>
            <Text type="secondary" className={styles.secondaryLine}>
              协议：{record.protocol || '未声明'}
            </Text>
          </div>
        ),
      },
      {
        title: '能力',
        key: 'functions',
        width: 100,
        align: 'center',
        render: (_, record) => (
          <div className={styles.methodCount}>
            <Text strong>{record.functions.length}</Text>
            <Text type="secondary" className={styles.secondaryLine}>
              个方法
            </Text>
          </div>
        ),
      },
      {
        title: '操作',
        width: 100,
        fixed: 'right',
        render: (_, record) => <Button onClick={() => handleOpenDevice(record)}>审批</Button>,
      },
    ],
    [handleOpenDevice]
  )

  const templateColumns: TableColumnsType<AdminDeviceTemplateListItem> = useMemo(
    () => [
      {
        title: '模板',
        key: 'template',
        render: (_, record) => (
          <div className={styles.templateCell}>
            <Space wrap>
              <Text strong>{record.name}</Text>
              <Tag color={record.enabled ? 'green' : 'default'}>
                {record.enabled ? '已启用' : '已停用'}
              </Tag>
            </Space>
            <Text code copyable={{ text: record.id }}>
              {record.id}
            </Text>
          </div>
        ),
      },
      { title: '分类', dataIndex: 'category', width: 150 },
      {
        title: '素材轴',
        dataIndex: 'accepted_resource_axes',
        width: 260,
        render: (axes: DeviceTemplateResourceAxis[]) => (
          <Space wrap>
            {axes.map((axis) => (
              <Tag key={axis}>{axis}</Tag>
            ))}
          </Space>
        ),
      },
      {
        title: '函数',
        dataIndex: 'functions',
        width: 100,
        render: (functions: DeviceTemplateFunction[]) => `${functions.length} 个`,
      },
      { title: '已分配设备', dataIndex: 'assignment_count', width: 120 },
      {
        title: '操作',
        width: 190,
        render: (_, record) => (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => void handleOpenTemplate(record)}>
              编辑
            </Button>
            <Popconfirm
              title={record.enabled ? '停用模板？' : '启用模板？'}
              description={
                record.enabled && record.assignment_count > 0
                  ? `停用后运行时将排除该模板，当前有 ${record.assignment_count} 台设备已分配。`
                  : undefined
              }
              onConfirm={() => void handleSetTemplateEnabled(record, !record.enabled)}
            >
              <Button danger={record.enabled}>{record.enabled ? '停用' : '启用'}</Button>
            </Popconfirm>
            <Tooltip
              title={
                record.assignment_count > 0
                  ? `当前有 ${record.assignment_count} 台设备引用，需先解除引用`
                  : undefined
              }
            >
              <span>
                <Popconfirm
                  title="确认删除这个设备模板？"
                  description="删除后模板不会再出现在管理列表和运行时目录中。"
                  disabled={record.assignment_count > 0}
                  onConfirm={() => void handleDeleteTemplate(record)}
                >
                  <Button danger disabled={record.assignment_count > 0}>
                    删除
                  </Button>
                </Popconfirm>
              </span>
            </Tooltip>
          </Space>
        ),
      },
    ],
    [handleDeleteTemplate, handleOpenTemplate, handleSetTemplateEnabled]
  )

  const tokenColumns: TableColumnsType<DeviceCatalogToken> = useMemo(
    () => [
      {
        title: 'Token',
        dataIndex: 'token_id',
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text strong>{record.device_no}</Text>
            <Text type="secondary">{record.token_id}</Text>
          </Space>
        ),
      },
      { title: '客户端', dataIndex: 'client_type', width: 130 },
      { title: '环境', dataIndex: 'environment', width: 100 },
      {
        title: '状态',
        dataIndex: 'status',
        width: 100,
        render: (value: string) => <Tag color={value === 'active' ? 'green' : 'red'}>{value}</Tag>,
      },
      { title: '使用次数', dataIndex: 'usage_count', width: 100 },
      { title: '过期时间', dataIndex: 'expires_at', width: 190 },
      {
        title: '操作',
        width: 100,
        render: (_, record) => (
          <Button
            danger
            disabled={record.status !== 'active'}
            onClick={() => handleRevokeToken(record.token_id)}
          >
            撤销
          </Button>
        ),
      },
    ],
    [handleRevokeToken]
  )

  const snapshotColumns: TableColumnsType<DeviceCatalogSnapshot> = useMemo(
    () => [
      {
        title: '快照',
        dataIndex: 'snapshot_id',
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text code>{record.snapshot_id}</Text>
            <Text type="secondary">{record.source}</Text>
          </Space>
        ),
      },
      { title: '环境', dataIndex: 'environment', width: 100 },
      { title: '场馆', dataIndex: 'venue_id', width: 100 },
      { title: '设备数', dataIndex: 'device_count', width: 90 },
      { title: '已测试', dataIndex: 'tested_count', width: 90 },
      { title: '已批准', dataIndex: 'approved_count', width: 90 },
      { title: '生成时间', dataIndex: 'generated_at', width: 190 },
    ],
    []
  )

  return (
    <div>
      <Space
        align="start"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={2}>设备能力目录</Title>
          <Text type="secondary">审批 AI Engine 同步的硬件设备与方法，控制编辑器可见范围。</Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            void loadDevices()
            void loadSnapshots()
          }}
        >
          刷新目录
        </Button>
      </Space>

      <Alert
        showIcon
        type="info"
        style={{ marginBottom: 16 }}
        message="AI Engine 只负责上报发现到的设备能力，后台审批状态不会被同步覆盖。"
      />

      <Card style={{ marginBottom: 16 }} loading={loadingSnapshots}>
        <Space direction="vertical" size={4}>
          <Text strong>最近同步状态</Text>
          {latestSnapshot ? (
            <Space wrap>
              <Tag color="blue">{latestSnapshot.environment}</Tag>
              <Text>来源：{latestSnapshot.source}</Text>
              <Text>设备数：{latestSnapshot.device_count}</Text>
              <Text>已测试：{latestSnapshot.tested_count}</Text>
              <Text>已批准：{latestSnapshot.approved_count}</Text>
              <Text type="secondary">生成时间：{latestSnapshot.generated_at}</Text>
            </Space>
          ) : (
            <Text type="secondary">暂无 AI Engine 设备目录同步快照</Text>
          )}
        </Space>
      </Card>

      <Tabs
        items={[
          {
            key: 'templates',
            label: '标准设备模板',
            children: (
              <Card
                title="模板定义"
                extra={
                  <Space>
                    <Button icon={<ReloadOutlined />} onClick={() => void loadTemplates()}>
                      刷新
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTemplate}>
                      新建模板
                    </Button>
                  </Space>
                }
              >
                <Alert
                  showIcon
                  type="info"
                  className={styles.sectionAlert}
                  message="模板只描述编辑器能力、素材轴和参数 Schema；不填写 MQTT 路由、物理设备 ID、凭据或最终媒体 URL。"
                />
                <Table
                  rowKey="id"
                  loading={loadingTemplates}
                  columns={templateColumns}
                  dataSource={templates}
                  scroll={{ x: 1050 }}
                />
              </Card>
            ),
          },
          {
            key: 'devices',
            label: '设备与方法审批',
            children: (
              <Card>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Space wrap>
                    <Text type="secondary">用户显示</Text>
                    <Select
                      value={deviceFilters.visibility}
                      onChange={(value: DeviceFilterValues['visibility']) => {
                        setDeviceFilters((current) => ({
                          ...current,
                          visibility: value,
                        }))
                        setDevicePagination((current) => ({
                          ...current,
                          current: 1,
                        }))
                      }}
                      style={{ width: 150 }}
                      options={[
                        { value: 'visible', label: '仅显示设备' },
                        { value: 'hidden', label: '仅隐藏设备' },
                        { value: 'all', label: '全部设备' },
                      ]}
                    />
                    <Text type="secondary">目录状态</Text>
                    <Select
                      value={deviceFilters.sourceState}
                      onChange={(value: DeviceFilterValues['sourceState']) => {
                        setDeviceFilters((current) => ({
                          ...current,
                          sourceState: value,
                          visibility: value === 'inactive' ? 'all' : current.visibility,
                        }))
                        setDevicePagination((current) => ({
                          ...current,
                          current: 1,
                        }))
                      }}
                      style={{ width: 150 }}
                      options={[
                        { value: 'active', label: '当前设备' },
                        { value: 'inactive', label: '历史设备' },
                        { value: 'all', label: '全部记录' },
                      ]}
                    />
                    <Text type="secondary">设备归类</Text>
                    <Select
                      value={deviceFilters.deviceGroup}
                      onChange={(value: string) => {
                        setDeviceFilters((current) => ({
                          ...current,
                          deviceGroup: value,
                        }))
                        setDevicePagination((current) => ({
                          ...current,
                          current: 1,
                        }))
                      }}
                      style={{ width: 150 }}
                      options={[{ value: 'all', label: '全部归类' }, ...DEVICE_GROUP_OPTIONS]}
                    />
                    <Text type="secondary">地图摆放</Text>
                    <Select
                      value={deviceFilters.editorPlaceable}
                      onChange={(value: DeviceFilterValues['editorPlaceable']) => {
                        setDeviceFilters((current) => ({
                          ...current,
                          editorPlaceable: value,
                        }))
                        setDevicePagination((current) => ({
                          ...current,
                          current: 1,
                        }))
                      }}
                      style={{ width: 180 }}
                      options={[
                        { value: 'all', label: '全部设备' },
                        { value: 'placeable', label: '仅可摆放' },
                        { value: 'non_placeable', label: '仅服务类/不可摆放' },
                      ]}
                    />
                    <Text type="secondary">共 {devicePagination.total} 台</Text>
                  </Space>
                  <Table
                    className={styles.deviceCatalogTable}
                    rowKey="device_id"
                    size="middle"
                    tableLayout="fixed"
                    loading={loadingDevices}
                    columns={deviceColumns}
                    dataSource={devices}
                    scroll={{ x: 1460 }}
                    pagination={{
                      current: devicePagination.current,
                      pageSize: devicePagination.pageSize,
                      total: devicePagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions: [10, 20, 50, 100],
                      showTotal: (total) => `共 ${total} 台设备`,
                      onChange: (page, pageSize) => {
                        setDevicePagination((current) => ({
                          ...current,
                          current: page,
                          pageSize,
                        }))
                      },
                    }}
                    expandable={{
                      expandedRowRender: (record) => (
                        <Table
                          className={styles.deviceCatalogTable}
                          rowKey="id"
                          size="small"
                          tableLayout="fixed"
                          pagination={false}
                          columns={functionColumns}
                          dataSource={record.functions}
                          scroll={{ x: 1050 }}
                        />
                      ),
                    }}
                  />
                </Space>
              </Card>
            ),
          },
          {
            key: 'snapshots',
            label: '同步快照',
            children: (
              <Card
                title="AI Engine 同步记录"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={() => void loadSnapshots()}>
                    刷新同步状态
                  </Button>
                }
              >
                <Table
                  rowKey="snapshot_id"
                  loading={loadingSnapshots}
                  columns={snapshotColumns}
                  dataSource={snapshots}
                />
              </Card>
            ),
          },
          {
            key: 'tokens',
            label: '目录 Token',
            children: (
              <Card
                title="编辑器访问 Token"
                extra={
                  <Button
                    icon={<KeyOutlined />}
                    type="primary"
                    onClick={() => {
                      tokenForm.setFieldsValue({
                        client_type: 'osf-editor',
                        environment: 'lab',
                        expires_in_days: 365,
                        scopes: DEFAULT_SCOPES,
                      })
                      setTokenModalOpen(true)
                    }}
                  >
                    创建 Token
                  </Button>
                }
              >
                <Table
                  rowKey="token_id"
                  loading={loadingTokens}
                  columns={tokenColumns}
                  dataSource={tokens}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={editingTemplate ? `编辑模板：${editingTemplate.name}` : '新建标准设备模板'}
        open={templateModalOpen}
        width={820}
        onCancel={() => {
          setTemplateModalOpen(false)
          setEditingTemplate(null)
          templateForm.resetFields()
        }}
        onOk={() => void handleSaveTemplate()}
        okText="保存模板"
        destroyOnClose
      >
        <Form form={templateForm} layout="vertical">
          <div className={styles.formGrid}>
            <Form.Item
              name="id"
              label="稳定模板 ID"
              rules={[
                { required: true, message: '请输入稳定模板 ID' },
                {
                  pattern: /^[a-z][a-z0-9_]*$/,
                  message: '仅支持小写字母、数字和下划线',
                },
              ]}
              extra="ID 保存后不可改名；screen 与 handheld_screen 必须保持独立。"
            >
              <Input disabled={!!editingTemplate} placeholder="例如 screen" />
            </Form.Item>
            <Form.Item name="name" label="模板名称" rules={[{ required: true }]}>
              <Input placeholder="例如 标准屏幕" />
            </Form.Item>
            <Form.Item name="category" label="分类" rules={[{ required: true }]}>
              <Input placeholder="例如 display" />
            </Form.Item>
            <Form.Item name="editor_plugin_id" label="编辑器插件 ID">
              <Input placeholder="可选，例如 display.basic" />
            </Form.Item>
          </div>
          <Form.Item name="tags" label="标签">
            <Select mode="tags" tokenSeparators={[',', '，']} />
          </Form.Item>
          <Form.Item name="accepted_resource_axes" label="接受的素材轴">
            <Select mode="multiple" options={RESOURCE_AXIS_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="functions_json"
            label="函数与参数 Schema（JSON）"
            rules={[{ required: true, message: '请输入 JSON 数组' }]}
            extra="每项仅包含 name、label、parameters；schema_hash 由服务端根据规范化 parameters 自动生成。"
          >
            <TextArea rows={12} className={styles.jsonEditor} spellCheck={false} />
          </Form.Item>
          <Form.Item name="enabled" label="模板状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
        {editingTemplate && (
          <Card size="small" title={`已分配设备（${editingTemplate.assignment_count}）`}>
            {editingTemplate.assigned_devices.length > 0 ? (
              <Table
                size="small"
                rowKey="device_id"
                pagination={false}
                dataSource={editingTemplate.assigned_devices}
                columns={[
                  { title: '设备', dataIndex: 'display_name' },
                  { title: '设备 ID', dataIndex: 'device_id' },
                  { title: '类型', dataIndex: 'device_type', width: 140 },
                  { title: '状态', dataIndex: 'status', width: 100 },
                ]}
              />
            ) : (
              <Text type="secondary">暂无具体设备使用此模板</Text>
            )}
          </Card>
        )}
      </Modal>

      <Modal
        title="设备审批"
        open={!!editingDevice}
        onCancel={() => {
          setEditingDevice(null)
          deviceForm.resetFields()
        }}
        footer={[
          <Popconfirm
            key="restore"
            title="恢复智能识别？"
            description="将清除管理员维护的归类、标签和可见性，重新使用系统规则。"
            okText="恢复"
            cancelText="取消"
            onConfirm={() => void handleRestoreDeviceClassification()}
          >
            <Button>恢复智能识别</Button>
          </Popconfirm>,
          <Button
            key="cancel"
            onClick={() => {
              setEditingDevice(null)
              deviceForm.resetFields()
            }}
          >
            取消
          </Button>,
          <Button key="save" type="primary" onClick={() => void handleSaveDevice()}>
            保存
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={deviceForm} layout="vertical">
          <Form.Item name="display_name" label="展示名称">
            <Input />
          </Form.Item>
          {editingDevice && (
            <Alert
              showIcon
              type={editingDevice.classification_source === 'manual_override' ? 'success' : 'info'}
              message={
                editingDevice.classification_source === 'manual_override'
                  ? '当前归类由管理员维护'
                  : `系统已根据设备信息智能识别，置信度 ${Math.round(editingDevice.classification_confidence * 100)}%`
              }
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item
            name="template_id"
            label="标准设备模板"
            extra="选择模板仅建立可视化分配关系；模板停用后不会进入运行时目录。"
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="未分配模板"
              options={templates.map((template) => ({
                value: template.id,
                label: `${template.name} (${template.id})${template.enabled ? '' : ' · 已停用'}`,
                disabled: !template.enabled && template.id !== editingDevice?.template_id,
              }))}
            />
          </Form.Item>
          <Form.Item name="category" label="编辑器分类">
            <Select
              options={[
                'screen',
                'audio',
                'light',
                'atmosphere',
                'sensor',
                'io',
                'positioning',
                'handheld',
                'gateway',
                'camera',
                'custom',
              ].map((value) => ({ value, label: value }))}
            />
          </Form.Item>
          <Form.Item
            name="device_group"
            label="用户设备归类"
            rules={[{ required: true, message: '请选择设备归类' }]}
          >
            <Select options={DEVICE_GROUP_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="tags"
            label="设备标签"
            extra="选择“内部设备”或“不显示”后，设备会自动从用户侧隐藏。"
          >
            <Select
              mode="tags"
              tokenSeparators={[',', '，']}
              options={DEVICE_TAG_OPTIONS.map((value) => ({
                value,
                label: value,
              }))}
              onChange={(values: string[]) => {
                if (values.some((value) => HIDDEN_DEVICE_TAGS.has(value))) {
                  deviceForm.setFieldValue('user_visible', false)
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="user_visible"
            label="用户侧显示"
            valuePropName="checked"
            extra={visibilityLockedByTag ? '当前标签要求该设备保持隐藏。' : undefined}
          >
            <Switch
              checkedChildren="显示"
              unCheckedChildren="隐藏"
              disabled={visibilityLockedByTag}
            />
          </Form.Item>
          <Form.Item name="status" label="测试状态">
            <Select
              options={['registered', 'tested', 'disabled', 'offline'].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
          <Form.Item name="approved_for_editor" label="批准进入编辑器" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="editor_placeable" label="允许在地图中摆放" valuePropName="checked">
            <Switch checkedChildren="可摆放" unCheckedChildren="服务类" />
          </Form.Item>
          <Form.Item name="environment" label="环境">
            <Select
              options={['lab', 'venue', 'production', 'staging'].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
          <Form.Item name="protocol" label="协议">
            <Input placeholder="mqtt / artnet / gpio" />
          </Form.Item>
          <Form.Item name="mobility" label="移动性">
            <Input placeholder="fixed / portable / gateway" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="方法审批"
        open={!!editingFunction}
        onCancel={() => {
          setEditingFunction(null)
          functionForm.resetFields()
        }}
        onOk={() => void handleSaveFunction()}
        destroyOnClose
      >
        <Form form={functionForm} layout="vertical">
          <Form.Item name="label" label="展示名称">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="status" label="测试状态">
            <Select
              options={['registered', 'tested', 'disabled'].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
          <Form.Item name="approved_for_editor" label="批准进入编辑器" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="safety_level" label="安全等级">
            <Select
              options={['safe', 'normal', 'destructive', 'restricted'].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
          <Form.Item name="dry_run_supported" label="支持 dry run" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="创建设备目录 Token"
        open={tokenModalOpen}
        onCancel={() => {
          setTokenModalOpen(false)
          setCreatedToken(null)
          tokenForm.resetFields()
        }}
        onOk={() => void handleCreateToken()}
        destroyOnClose
      >
        <Form form={tokenForm} layout="vertical">
          <Form.Item name="device_no" label="设备号 / 客户端号" rules={[{ required: true }]}>
            <Input placeholder="editor-lab-001" />
          </Form.Item>
          <Form.Item name="client_type" label="客户端类型" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="environment" label="环境" rules={[{ required: true }]}>
            <Select
              options={['lab', 'venue', 'production', 'staging'].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
          <Form.Item name="merchant_id" label="商户 ID">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="venue_id" label="场馆 ID">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="room_id" label="房间 ID">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="scopes" label="权限" rules={[{ required: true }]}>
            <Select
              mode="tags"
              options={DEFAULT_SCOPES.map((value) => ({ value, label: value }))}
            />
          </Form.Item>
          <Form.Item name="expires_in_days" label="有效期天数" rules={[{ required: true }]}>
            <InputNumber min={1} max={730} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
        {createdToken && (
          <Alert
            showIcon
            type="warning"
            icon={<SafetyCertificateOutlined />}
            message="请立即复制保存，关闭后无法再次查看明文 token。"
            description={
              <Paragraph copyable style={{ marginBottom: 0 }}>
                {createdToken.access_token}
              </Paragraph>
            }
          />
        )}
      </Modal>
    </div>
  )
}

export default DeviceCatalogManagement
